/**
 * TypeDetailDialog - Dialog for viewing and editing quote type entries
 * Extracted from App.js for better maintainability
 */
import React, { useState, useEffect } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import { X, Edit3, Trash2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency, formatNumber } from "@/utils/formatters";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TypeDetailDialog = ({ type, isOpen, onClose, onRefresh }) => {
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ width: "", height: "", cost: "" });
  const [isDeleting, setIsDeleting] = useState(false);
  const [localType, setLocalType] = useState(null);

  // Update local type when prop changes
  useEffect(() => {
    if (type) {
      setLocalType(type);
    }
  }, [type]);

  // Fetch fresh type data
  const refreshLocalType = async () => {
    if (!type?.id) return;
    try {
      const response = await api.get(`${API}/quote-types`);
      const updatedType = response.data.find(t => t.id === type.id);
      if (updatedType) {
        setLocalType(updatedType);
      }
      onRefresh(); // Also refresh parent
    } catch (error) {
      console.error("Error refreshing type:", error?.message || error);
    }
  };

  if (!localType) return null;

  const handleEditEntry = (entry, index) => {
    setEditingEntry(index);
    setEditForm({
      width: entry.width.toString(),
      height: entry.height.toString(),
      cost: entry.cost.toString()
    });
  };

  const handleSaveEntry = async (index) => {
    try {
      await api.put(`${API}/quote-types/${localType.id}/entries/${index}`, {
        width: parseFloat(editForm.width),
        height: parseFloat(editForm.height),
        cost: parseFloat(editForm.cost)
      });
      toast.success("Cotización actualizada");
      setEditingEntry(null);
      await refreshLocalType(); // Refresh local data immediately
    } catch (error) {
      toast.error("Error al actualizar cotización");
    }
  };

  const handleDeleteEntry = async (index) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    
    setIsDeleting(true);
    try {
      const response = await api.delete(`${API}/quote-types/${localType.id}/entries/${index}`);
      if (response.data.type_deleted) {
        toast.success("Tipo eliminado (no quedaban cotizaciones)");
        onClose();
        onRefresh();
      } else {
        toast.success("Cotización eliminada");
        await refreshLocalType(); // Refresh local data immediately
      }
    } catch (error) {
      toast.error("Error al eliminar cotización");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditForm({ width: "", height: "", cost: "" });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900">
            {localType.name}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <Badge variant={localType.material === "Aluminio" ? "default" : "secondary"}>
              {localType.material}
            </Badge>
            {localType.series && <Badge variant="outline">{localType.series}</Badge>}
            {localType.color && <Badge variant="outline">{localType.color}</Badge>}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-3 gap-4 my-4">
          <div className="bg-slate-50 rounded-sm p-4 text-center">
            <p className="data-label mb-1">Promedio/m²</p>
            <p className="font-mono font-bold text-lg text-slate-900">
              {formatCurrency(localType.average_cost_per_m2)}
            </p>
          </div>
          <div className="bg-emerald-50 rounded-sm p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-1">Mínimo/m²</p>
            <p className="font-mono font-bold text-lg text-emerald-700">
              {formatCurrency(localType.min_cost_per_m2)}
            </p>
          </div>
          <div className="bg-amber-50 rounded-sm p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-1">Máximo/m²</p>
            <p className="font-mono font-bold text-lg text-amber-700">
              {formatCurrency(localType.max_cost_per_m2)}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
          <span>{localType.entries?.length || 0} cotizaciones registradas</span>
          <span>Área total: {formatNumber(localType.total_area, 2)} m²</span>
        </div>

        <ScrollArea className="h-[350px] border rounded-sm">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow>
                <TableHead className="w-[80px]">Ancho (m)</TableHead>
                <TableHead className="w-[80px]">Alto (m)</TableHead>
                <TableHead className="text-right w-[90px]">Área (m²)</TableHead>
                <TableHead className="text-right w-[100px]">Costo</TableHead>
                <TableHead className="text-right w-[90px]">$/m²</TableHead>
                <TableHead>Archivo</TableHead>
                <TableHead className="text-right w-[100px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {localType.entries?.map((entry, idx) => (
                <TableRow key={`entry-${localType.id}-${idx}`} className="table-row-hover">
                  {editingEntry === idx ? (
                    <>
                      <TableCell className="p-1">
                        <Input
                          type="number"
                          step="0.001"
                          value={editForm.width}
                          onChange={(e) => setEditForm({...editForm, width: e.target.value})}
                          className="h-8 w-20 font-mono text-sm"
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <Input
                          type="number"
                          step="0.001"
                          value={editForm.height}
                          onChange={(e) => setEditForm({...editForm, height: e.target.value})}
                          className="h-8 w-20 font-mono text-sm"
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-400 p-1">
                        {editForm.width && editForm.height ? formatNumber(parseFloat(editForm.width) * parseFloat(editForm.height), 4) : "—"}
                      </TableCell>
                      <TableCell className="p-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={editForm.cost}
                          onChange={(e) => setEditForm({...editForm, cost: e.target.value})}
                          className="h-8 w-24 font-mono text-sm"
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-400 p-1">
                        {editForm.width && editForm.height && editForm.cost 
                          ? formatCurrency(parseFloat(editForm.cost) / (parseFloat(editForm.width) * parseFloat(editForm.height)))
                          : "—"}
                      </TableCell>
                      <TableCell className="p-1"></TableCell>
                      <TableCell className="text-right p-1">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleCancelEdit}>
                            <X className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="default" className="h-7 w-7 p-0" onClick={() => handleSaveEntry(idx)}>
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="font-mono">{formatNumber(entry.width, 3)}</TableCell>
                      <TableCell className="font-mono">{formatNumber(entry.height, 3)}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(entry.area, 4)}</TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        {formatCurrency(entry.cost)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(entry.cost_per_m2)}
                      </TableCell>
                      <TableCell className="text-slate-500 text-xs truncate max-w-[120px]">
                        {entry.source_file}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600"
                            onClick={() => handleEditEntry(entry, idx)}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 w-7 p-0 text-slate-500 hover:text-red-600"
                            onClick={() => handleDeleteEntry(idx)}
                            disabled={isDeleting}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TypeDetailDialog;
